/**
 * Contact buttons fixed at the bottom right of the page: Zalo (a chat with the shop's phone number) and Facebook
 * Messenger (the shop's page), as set in /admin › Liên hệ (server contact.py, read from /api/status). Nothing shows
 * until one is set. Each opens the official chat page (zalo.me, m.me) in a new tab; the page sends nothing there.
 */
import { useEffect } from "react";
import type { Contact } from "../api";
import { CONTACT } from "../messages";

const BUTTON = 52;                                     // px, .contact-btn
const GAP = 12;                                        // px, .contact-fab gap
const EDGE = 16;                                       // px, .contact-fab bottom offset (desktop)

export const zaloUrl = (phone: string) => `https://zalo.me/${encodeURIComponent(phone)}`;
export const messengerUrl = (page: string) => `https://m.me/${encodeURIComponent(page)}`;

/** The Zalo mark: a white chat bubble with the blue wordmark (drawn as strokes, no font needed). */
function ZaloIcon() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path fill="#fff" d="M24 7C12.95 7 4 14.16 4 23c0 4.4 2.2 8.38 5.77 11.27-.36 2.3-1.5 4.67-3.27 6.43 3.47.02 6.68-1.17 9.03-2.86C18.3 38.6 21.08 39 24 39c11.05 0 20-7.16 20-16S35.05 7 24 7Z" />
      <path fill="none" stroke="#0068ff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
            d="M12.6 19h5.5l-5.5 8.3h5.7M25 21.8v5.5M25 24.55a2.5 2.7 0 1 1-5 0a2.5 2.7 0 1 1 5 0M28 18.3v9M35.6 24.55a2.7 2.7 0 1 1-5.4 0a2.7 2.7 0 1 1 5.4 0" />
    </svg>
  );
}

function MessengerIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.19 5.44 3.14 7.17.16.14.26.35.27.57l.05 1.78c.02.57.6.94 1.12.71l1.99-.88c.17-.07.36-.09.54-.04.91.25 1.88.39 2.89.39 5.64 0 10-4.13 10-9.7S17.64 2 12 2Z" />
      <path fill="var(--messenger-bolt, #0a7cff)" d="m6 14.53 2.94-4.66a1.5 1.5 0 0 1 2.17-.4l2.34 1.75c.21.16.51.16.72 0l3.16-2.4c.42-.32.97.18.69.63l-2.94 4.66a1.5 1.5 0 0 1-2.17.4l-2.34-1.75a.6.6 0 0 0-.72 0l-3.16 2.4c-.42.32-.97-.18-.69-.63Z" />
    </svg>
  );
}

export function ContactButtons({ contact }: { contact?: Contact | null }) {
  const count = (contact?.zalo ? 1 : 0) + (contact?.messenger ? 1 : 0);
  // while the buttons show, the layout keeps --contact-space free at the bottom (styles.css: .has-contact …) so they
  // never sit on the last lines of a column
  useEffect(() => {
    if (!count) return;
    const root = document.documentElement;
    root.classList.add("has-contact");
    root.style.setProperty("--contact-space", `${EDGE + count * BUTTON + (count - 1) * GAP + 8}px`);
    return () => {
      root.classList.remove("has-contact");
      root.style.removeProperty("--contact-space");
    };
  }, [count]);
  if (!contact || !count) return null;
  return (
    <nav className="contact-fab" aria-label={CONTACT.label}>
      {contact.zalo && (
        <a className="contact-btn contact-zalo" href={zaloUrl(contact.zalo)} target="_blank" rel="noopener noreferrer"
           aria-label={CONTACT.zalo} title={CONTACT.zalo}>
          <ZaloIcon />
        </a>
      )}
      {contact.messenger && (
        <a className="contact-btn contact-messenger" href={messengerUrl(contact.messenger)} target="_blank"
           rel="noopener noreferrer" aria-label={CONTACT.messenger} title={CONTACT.messenger}>
          <MessengerIcon />
        </a>
      )}
    </nav>
  );
}
